import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { appConfig } from '../../config/appConfig';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showValuationDropdown, setShowValuationDropdown] = useState(false);
  const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isValuationMobileOpen, setIsValuationMobileOpen] = useState(false);
  const [isIndustriesMobileOpen, setIsIndustriesMobileOpen] = useState(false);
  const [isAboutMobileOpen, setIsAboutMobileOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const valuationHoverTimeoutRef = useRef(null);
  const industriesDropdownRef = useRef(null);
  const industriesHoverTimeoutRef = useRef(null);
  const aboutDropdownRef = useRef(null);
  const aboutHoverTimeoutRef = useRef(null);
  const headerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (aboutHoverTimeoutRef.current) clearTimeout(aboutHoverTimeoutRef.current);
      if (valuationHoverTimeoutRef.current) clearTimeout(valuationHoverTimeoutRef.current);
      if (industriesHoverTimeoutRef.current) clearTimeout(industriesHoverTimeoutRef.current);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && headerRef.current.contains(event.target)) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowValuationDropdown(false);
      }
      if (industriesDropdownRef.current && !industriesDropdownRef.current.contains(event.target)) {
        setShowIndustriesDropdown(false);
      }
      if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(event.target)) {
        setShowAboutDropdown(false);
      }
      // Close mobile menu when clicking outside header area
      if (isMobileMenuOpen && !document.getElementById('mobile-menu')?.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Measure header height for correct mobile menu offset
  useEffect(() => {
    const measure = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
      setIsMobile(window.matchMedia('(max-width: 640px)').matches);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, []);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const { style } = document.body;
    const previousOverflow = style.overflow;

    if (isMobileMenuOpen) {
      style.overflow = 'hidden';
    } else {
      style.overflow = '';
    }

    return () => {
      style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  // Collapse mobile accordions when the mobile menu closes
  useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsValuationMobileOpen(false);
      setIsIndustriesMobileOpen(false);
      setIsAboutMobileOpen(false);
    }
  }, [isMobileMenuOpen]);

  // When mobile and header is in transparent state, use light text for contrast
  const useLightText = isMobile && !isScrolled && !isMobileMenuOpen;
  const isValuationRouteActive =
    location.pathname.includes('/valuation') ||
    location.pathname.includes('/services/business-valuation') ||
    location.pathname.includes('/services/buyer-sourcing');
  const industryPaths = Object.values(appConfig.routes.industryPages || {});
  const isIndustriesRouteActive =
    location.pathname === appConfig.routes.industries ||
    industryPaths.some((path) => location.pathname.startsWith(path));
  const isProcessRouteActive = location.pathname.includes('/process');
  const isSuccessStoriesRouteActive = location.pathname.includes('/success-stories');
  const isAboutRouteActive =
    location.pathname.includes('/about') ||
    location.pathname.includes('/team') ||
    location.pathname.includes('/faq');

  const servicesMenuItems = [
    {
      to: appConfig.routes.valuation,
      title: 'Get Free Valuation',
      subtitle: 'Calculate your business value instantly',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.services.businessValuation,
      title: 'How We Value Businesses',
      subtitle: 'Our methodology explained',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.services.buyerSourcing,
      title: 'Buyer Sourcing',
      subtitle: '200,000 qualified buyers',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
  ];

  const aboutMenuItems = [
    {
      to: appConfig.routes.about,
      title: 'About AMIVA',
      subtitle: 'Our story and mission',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.team,
      title: 'Our Team',
      subtitle: 'Meet the experts',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.faq,
      title: 'FAQs',
      subtitle: 'Common questions',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ].filter((item) => item.to !== appConfig.routes.team || appConfig.features.teamPage);

  const industriesMenuItems = [
    {
      to: appConfig.routes.industryPages?.dental,
      title: 'Dental Practice Advisory',
      subtitle: 'DSO roll-ups and dental valuation multiples',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.industryPages?.saas,
      title: 'SaaS & Software',
      subtitle: 'ARR quality and premium SaaS exits',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.industryPages?.manufacturing,
      title: 'Manufacturing',
      subtitle: 'Certifications, backlog, and utilization',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.industryPages?.hvac,
      title: 'HVAC & Home Services',
      subtitle: 'Membership revenue and route density',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.industryPages?.commercialCleaning,
      title: 'Commercial Cleaning',
      subtitle: 'Contract stability and workforce performance',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      to: appConfig.routes.industryPages?.crossBorder,
      title: 'Cross-Border (US–EU)',
      subtitle: 'Dual-continent buyer orchestration',
      icon: (
        <svg className="w-4 h-4 text-amiva-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ].filter((item) => Boolean(item.to));

  const mobileTriggerColorClasses = (() => {
    if (isMobileMenuOpen) {
      return 'text-gray-800 hover:bg-gray-100';
    }
    if (useLightText) {
      return 'text-amiva-primary hover:bg-white/60';
    }
    return 'text-gray-700 hover:bg-gray-100';
  })();

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled ? 'backdrop-blur-xl shadow-xl py-4' : 'backdrop-blur-md py-6'}`}
        style={{
          backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
          borderBottom: isScrolled ? '1px solid rgba(0, 0, 0, 0.05)' : '1px solid transparent',
        }}
      >
        <div className="flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={appConfig.routes.home} className="flex h-14 sm:h-16 items-center group">
            <img
              src="/amiva-logo.png"
              alt="AMIVA+ Boutique M&A Advisory - Sell Your Business Fast"
              className="h-full w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Process - How it works */}
            <Link
              to={appConfig.routes.process}
              className={`font-semibold text-base transition-all duration-300 relative ${
                isProcessRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
              }`}
            >
              Process
              {isProcessRouteActive && (
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-amiva-primary rounded-full"></span>
              )}
            </Link>

            {/* Success Stories - Social proof */}
            <Link
              to={appConfig.routes.successStories}
              className={`font-semibold text-base transition-all duration-300 relative ${
                isSuccessStoriesRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
              }`}
            >
              Success
              {isSuccessStoriesRouteActive && (
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-amiva-primary rounded-full"></span>
              )}
            </Link>

            {/* Services Dropdown - Value proposition */}
            <div
              className="relative flex items-center px-2 -mx-2 rounded-md"
              ref={dropdownRef}
              onMouseEnter={() => {
                if (valuationHoverTimeoutRef.current) clearTimeout(valuationHoverTimeoutRef.current);
                setShowValuationDropdown(true);
              }}
              onMouseLeave={() => {
                if (valuationHoverTimeoutRef.current) clearTimeout(valuationHoverTimeoutRef.current);
                valuationHoverTimeoutRef.current = setTimeout(() => setShowValuationDropdown(false), 180);
              }}
              onFocus={() => setShowValuationDropdown(true)}
              onBlur={() => {
                if (valuationHoverTimeoutRef.current) clearTimeout(valuationHoverTimeoutRef.current);
                valuationHoverTimeoutRef.current = setTimeout(() => setShowValuationDropdown(false), 180);
              }}
            >
              <Link
                to={appConfig.routes.valuation}
                onClick={() => setShowValuationDropdown(false)}
                className={`font-semibold text-base transition-all duration-300 relative ${
                  isValuationRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
                }`}
              >
                Services
                {isValuationRouteActive && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-amiva-primary rounded-full"></span>
                )}
              </Link>
              <button
                aria-label="Toggle valuation menu"
                aria-haspopup="menu"
                aria-expanded={showValuationDropdown}
                onClick={() => setShowValuationDropdown(!showValuationDropdown)}
                className={`ml-1 p-2 -m-2 rounded-md transition-colors duration-300 hover:bg-gray-100 ${
                  isValuationRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
                }`}
              >
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${showValuationDropdown ? 'rotate-180' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Services Dropdown Menu */}
              {showValuationDropdown && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Our Services</div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-2">
                      {servicesMenuItems.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setShowValuationDropdown(false)}
                          className="flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amiva-primary/10 flex items-center justify-center group-hover:bg-amiva-primary/20 transition-colors mt-0.5">
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm group-hover:text-amiva-primary transition-colors mb-1">
                              {item.title}
                            </div>
                            <div className="text-xs text-gray-600 leading-relaxed">{item.subtitle}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Industries Dropdown - Specialization */}
            <div
              className="relative flex items-center px-2 -mx-2 rounded-md"
              ref={industriesDropdownRef}
              onMouseEnter={() => {
                if (industriesHoverTimeoutRef.current) clearTimeout(industriesHoverTimeoutRef.current);
                setShowIndustriesDropdown(true);
              }}
              onMouseLeave={() => {
                if (industriesHoverTimeoutRef.current) clearTimeout(industriesHoverTimeoutRef.current);
                industriesHoverTimeoutRef.current = setTimeout(() => setShowIndustriesDropdown(false), 180);
              }}
              onFocus={() => setShowIndustriesDropdown(true)}
              onBlur={() => {
                if (industriesHoverTimeoutRef.current) clearTimeout(industriesHoverTimeoutRef.current);
                industriesHoverTimeoutRef.current = setTimeout(() => setShowIndustriesDropdown(false), 180);
              }}
            >
              <Link
                to={appConfig.routes.industries}
                onClick={() => setShowIndustriesDropdown(false)}
                className={`font-semibold text-base transition-all duration-300 relative ${
                  isIndustriesRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
                }`}
              >
                Industries
                {isIndustriesRouteActive && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-amiva-primary rounded-full"></span>
                )}
              </Link>
              <button
                aria-label="Toggle industries menu"
                aria-haspopup="menu"
                aria-expanded={showIndustriesDropdown}
                onClick={() => setShowIndustriesDropdown(!showIndustriesDropdown)}
                className={`ml-1 p-2 -m-2 rounded-md transition-colors duration-300 hover:bg-gray-100 ${
                  isIndustriesRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
                }`}
              >
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${showIndustriesDropdown ? 'rotate-180' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {showIndustriesDropdown && industriesMenuItems.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-[540px] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">By Industry</div>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-3">
                      {industriesMenuItems.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setShowIndustriesDropdown(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amiva-primary/10 flex items-center justify-center group-hover:bg-amiva-primary/20 transition-colors">
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm group-hover:text-amiva-primary transition-colors truncate">
                              {item.title}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-gray-100">
                    <Link
                      to={appConfig.routes.industries}
                      onClick={() => setShowIndustriesDropdown(false)}
                      className="block px-6 py-3 text-center text-sm font-semibold text-amiva-primary hover:bg-gray-50 transition-colors duration-200"
                    >
                      View all industries →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* About Dropdown - Trust & credibility */}
            <div
              className="relative flex items-center px-2 -mx-2 rounded-md"
              ref={aboutDropdownRef}
              onMouseEnter={() => {
                if (aboutHoverTimeoutRef.current) clearTimeout(aboutHoverTimeoutRef.current);
                setShowAboutDropdown(true);
              }}
              onMouseLeave={() => {
                if (aboutHoverTimeoutRef.current) clearTimeout(aboutHoverTimeoutRef.current);
                aboutHoverTimeoutRef.current = setTimeout(() => setShowAboutDropdown(false), 180);
              }}
              onFocus={() => setShowAboutDropdown(true)}
              onBlur={() => {
                if (aboutHoverTimeoutRef.current) clearTimeout(aboutHoverTimeoutRef.current);
                aboutHoverTimeoutRef.current = setTimeout(() => setShowAboutDropdown(false), 180);
              }}
            >
              <Link
                to={appConfig.routes.about}
                onClick={() => setShowAboutDropdown(false)}
                className={`font-semibold text-base transition-all duration-300 relative ${
                  isAboutRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
                }`}
              >
                About
                {isAboutRouteActive && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-amiva-primary rounded-full"></span>
                )}
              </Link>
              <button
                aria-label="Toggle about menu"
                aria-haspopup="menu"
                aria-expanded={showAboutDropdown}
                onClick={() => setShowAboutDropdown(!showAboutDropdown)}
                className={`ml-1 p-2 -m-2 rounded-md transition-colors duration-300 hover:bg-gray-100 ${
                  isAboutRouteActive ? 'text-amiva-primary' : 'text-gray-700 hover:text-amiva-primary'
                }`}
              >
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${showAboutDropdown ? 'rotate-180' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* About Dropdown Menu - Complete */}
              {showAboutDropdown && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">About Us</div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-2">
                      {aboutMenuItems.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setShowAboutDropdown(false)}
                          className="flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amiva-primary/10 flex items-center justify-center group-hover:bg-amiva-primary/20 transition-colors mt-0.5">
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm group-hover:text-amiva-primary transition-colors mb-1">
                              {item.title}
                            </div>
                            <div className="text-xs text-gray-600 leading-relaxed">{item.subtitle}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Link to={appConfig.routes.valuation} className="btn-primary px-6 py-3 text-base font-semibold">
              Get My Valuation
            </Link>
            <a href={appConfig.site.signInUrl} target="_blank" rel="noreferrer">
              <button className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300">
                Sign In
              </button>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            aria-label="Open menu"
            className={`md:hidden inline-flex items-center justify-center rounded-lg p-2 transition-colors duration-200 ${mobileTriggerColorClasses}`}
            onClick={() => setIsMobileMenuOpen((v) => !v)}
          >
            <svg
              className={`h-6 w-6 transition-transform ${isMobileMenuOpen ? 'rotate-90' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-30 bg-gray-900/40 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
          <div
            id="mobile-menu"
            className="md:hidden fixed left-0 right-0 z-40 bg-white border-b border-gray-100 shadow-2xl rounded-b-3xl transition-transform duration-300 ease-out"
            style={{ top: headerHeight, maxHeight: `calc(100vh - ${headerHeight}px)` }}
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2 overflow-auto">
              {/* 1. Process */}
              <Link
                to={appConfig.routes.process}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  isProcessRouteActive ? 'bg-amiva-primary/10 text-amiva-primary' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Process
              </Link>

              {/* 2. Success Stories */}
              <Link
                to={appConfig.routes.successStories}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  isSuccessStoriesRouteActive
                    ? 'bg-amiva-primary/10 text-amiva-primary'
                    : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                Success Stories
              </Link>

              {/* 3. Services */}
              <button
                type="button"
                onClick={() => setIsValuationMobileOpen((v) => !v)}
                aria-expanded={isValuationMobileOpen}
                aria-controls="mobile-valuation-panel"
                className={`w-full flex items-center justify-between px-3 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  isValuationRouteActive ? 'bg-amiva-primary/10 text-amiva-primary' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span>Services</span>
                <svg
                  className={`w-4 h-4 transition-transform ${isValuationMobileOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {isValuationMobileOpen && (
                <div id="mobile-valuation-panel" className="pl-3 space-y-1">
                  {servicesMenuItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amiva-primary/10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* 4. Industries */}
              <button
                type="button"
                onClick={() => setIsIndustriesMobileOpen((v) => !v)}
                aria-expanded={isIndustriesMobileOpen}
                aria-controls="mobile-industries-panel"
                className={`w-full flex items-center justify-between px-3 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  isIndustriesRouteActive ? 'bg-amiva-primary/10 text-amiva-primary' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span>Industries</span>
                <svg
                  className={`w-4 h-4 transition-transform ${isIndustriesMobileOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {isIndustriesMobileOpen && (
                <div id="mobile-industries-panel" className="pl-3 space-y-1">
                  {industriesMenuItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amiva-primary/10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Link>
                  ))}
                  <Link
                    to={appConfig.routes.industries}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 rounded-lg text-amiva-primary hover:bg-gray-50 text-sm font-semibold text-center mt-2"
                  >
                    View all industries →
                  </Link>
                </div>
              )}

              {/* 5. About */}
              <button
                type="button"
                onClick={() => setIsAboutMobileOpen((v) => !v)}
                aria-expanded={isAboutMobileOpen}
                aria-controls="mobile-about-panel"
                className={`w-full flex items-center justify-between px-3 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  isAboutRouteActive ? 'bg-amiva-primary/10 text-amiva-primary' : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span>About</span>
                <svg
                  className={`w-4 h-4 transition-transform ${isAboutMobileOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {isAboutMobileOpen && (
                <div id="mobile-about-panel" className="pl-3 space-y-1">
                  {aboutMenuItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amiva-primary/10 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <span className="text-sm font-medium">{item.title}</span>
                    </Link>
                  ))}
                </div>
              )}

              <div className="pt-3 flex items-center gap-3">
                <Link
                  to={appConfig.routes.valuation}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-primary flex-1 text-center py-3"
                >
                  Get My Valuation
                </Link>
                <a
                  href={appConfig.site.signInUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-3 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  Sign In
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
